// Centered modal for viewing a screenshot at full size from the
// Activity Log Events page.
//
// Why a one-off composite (not a generic Modal primitive): SlidePanel
// is a right-drawer with form chrome (header/body/footer); the lightbox
// is centered, frameless, and image-first. Two structurally different
// dialogs is too thin a base to abstract over today — see the plan in
// the task body. Focus-trap, scroll-lock, and Esc-to-close patterns are
// mirrored verbatim from SlidePanel so future deduping, if any, has one
// shape to work from.
//
// Why no `width`: the lightbox is the surface the professional uses to
// confirm what they were doing in a contested time block, so legibility
// of small text in the captured screenshot is the dominant concern.
// Asking imgproxy for a fixed CSS-pixel width would force a server-side
// downscale of panoramic captures (a 5280-wide three-monitor source
// requested at 1600 is a 3.3× pixel reduction — small text blurs into
// illegibility) and would also under-serve retina/HiDPI workstations
// where the panel is already 1440-1600 CSS pixels wide and the device
// is multiplying that by 2. By omitting the prop we let the signed-URL
// request resolve to imgproxy's largest delivered variant for the
// source (capped upstream by IMGPROXY_MAX_SRC_FILE_SIZE per ADR 0038
// §Decision #6), and the browser scales the larger source down to the
// modal panel via `object-fit: contain`. Retina/HiDPI is covered as a
// side effect — the source pixel count exceeds the rendered CSS pixel
// count on every reasonable monitor for typical captures.
//
// Why the lightbox does not own the navigation index: the page knows
// the full set of visible screenshots (it derives `entries` from the
// table rows) and is the right layer to compute wrap-around. Keeping
// the lightbox a controlled component matches SlidePanel's shape and
// makes prev/next testable from the page level.

import { useCallback, useEffect, useId, useRef } from 'react';
import { ScreenshotImage } from './ScreenshotImage';
import { formatDateTimeInTz } from '@/lib/dates';
import styles from './ScreenshotLightbox.module.css';

export interface ScreenshotLightboxEntry {
  screenshotId: string;
  occurredAt: string;
  employeeName: string | null;
  altText: string;
}

interface ScreenshotLightboxProps {
  open: boolean;
  entries: ScreenshotLightboxEntry[];
  index: number;
  tz: string;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

export function ScreenshotLightbox({
  open,
  entries,
  index,
  tz,
  onClose,
  onPrev,
  onNext,
}: ScreenshotLightboxProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const safeIndex = entries.length === 0 ? 0 : Math.max(0, Math.min(index, entries.length - 1));
  const current: ScreenshotLightboxEntry | undefined = entries[safeIndex];
  const hasMultiple = entries.length > 1;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowRight' && hasMultiple) {
        onNext();
        return;
      }
      if (e.key === 'ArrowLeft' && hasMultiple) {
        onPrev();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose, onNext, onPrev, hasMultiple],
  );

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleKeyDown]);

  if (!open || !current) return null;

  const timestampLabel = formatDateTimeInTz(current.occurredAt, tz);
  const contextLabel = current.employeeName
    ? `${current.employeeName} · ${timestampLabel}`
    : timestampLabel;
  const dialogTitle = current.employeeName
    ? `Screenshot from ${current.employeeName}, ${timestampLabel}`
    : `Screenshot, ${timestampLabel}`;

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="screenshot-lightbox-overlay"
    >
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h2 id={titleId} className="sr-only">
          {dialogTitle}
        </h2>

        <div className={styles.header}>
          <span className={styles.context}>{contextLabel}</span>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close screenshot viewer"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M15 5L5 15M5 5l10 10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className={styles.imageArea}>
          {hasMultiple && (
            <button
              type="button"
              className={`${styles.navButton} ${styles.navButtonPrev}`}
              onClick={onPrev}
              aria-label="Previous screenshot"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </button>
          )}

          <ScreenshotImage
            screenshotId={current.screenshotId}
            alt={current.altText}
            loading="eager"
            className={styles.image}
          />

          {hasMultiple && (
            <button
              type="button"
              className={`${styles.navButton} ${styles.navButtonNext}`}
              onClick={onNext}
              aria-label="Next screenshot"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M9 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
